import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReservationTimeline } from "@/components/ReservationTimeline";
import { Plus, Pencil, Trash2, List, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { tenant_id: "", unit_id: "", rental_type: "monthly", start_date: "", end_date: "", rent_amount: 0, deposit_amount: 0, status: "active", notes: "" };
const statusColor = { active: "bg-[#EAF4F0] text-[#2D6A4F]", upcoming: "bg-gold-light text-terracotta", ended: "bg-secondary text-muted-foreground", cancelled: "bg-[#F9EAEB] text-destructive" };

export default function Reservations() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canDelete = user?.role !== "reception";
  const qc = useQueryClient();
  const { data: reservations = [] } = useQuery({ queryKey: ["reservations"], queryFn: () => api.get("/reservations").then((r) => r.data) });
  const { data: tenants = [] } = useQuery({ queryKey: ["tenants"], queryFn: () => api.get("/tenants").then((r) => r.data) });
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: () => api.get("/units").then((r) => r.data) });
  const [form, setForm] = useState(null);

  const tName = (id) => { const tn = tenants.find((x) => x.id === id); return tn ? `${tn.first_name} ${tn.last_name}` : "—"; };
  const uName = (id) => { const u = units.find((x) => x.id === id); return u ? u.number : "—"; };

  const openNew = () => setForm({ ...empty });
  const openUnit = (unitId, rent) => setForm({ ...empty, unit_id: unitId, rent_amount: rent, deposit_amount: rent });

  const priceFor = (u, type) => { if (!u) return 0; if (type === "daily") return u.price_daily || 0; if (type === "weekly") return u.price_weekly || 0; return u.base_rent || 0; };

  const save = async () => {
    if (!form.tenant_id || !form.unit_id || !form.start_date) { toast.error(t("res.tenant")); return; }
    const payload = { ...form, rent_amount: Number(form.rent_amount), deposit_amount: Number(form.deposit_amount) };
    if (form.id) await api.put(`/reservations/${form.id}`, payload);
    else await api.post("/reservations", payload);
    qc.invalidateQueries({ queryKey: ["reservations"] }); qc.invalidateQueries({ queryKey: ["units"] }); qc.invalidateQueries({ queryKey: ["stats"] });
    setForm(null); toast.success(t("common.save"));
  };

  const changeStatus = async (id, status) => {
    await api.patch(`/reservations/${id}/status?status=${status}`);
    qc.invalidateQueries({ queryKey: ["reservations"] }); qc.invalidateQueries({ queryKey: ["units"] }); qc.invalidateQueries({ queryKey: ["stats"] });
  };

  const remove = async (id) => {
    if (!window.confirm(t("res.deleteConfirm"))) return;
    await api.delete(`/reservations/${id}`);
    qc.invalidateQueries({ queryKey: ["reservations"] }); qc.invalidateQueries({ queryKey: ["units"] }); qc.invalidateQueries({ queryKey: ["stats"] });
    toast.success(t("common.delete"));
  };

  const selectedUnit = units.find((u) => u.id === form?.unit_id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">{t("res.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("res.subtitle")}</p>
        </div>
        <Button onClick={openNew} className="bg-teak hover:bg-teak-hover text-white" data-testid="add-reservation-btn">
          <Plus className="w-4 h-4 mr-1" /> {t("res.new")}
        </Button>
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="res-tab-list"><List className="w-4 h-4 mr-1" />{t("res.list")}</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="res-tab-calendar"><CalendarRange className="w-4 h-4 mr-1" />{t("res.calendar")}</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <Card className="border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t("res.unit")}</TableHead>
                  <TableHead>{t("res.tenant")}</TableHead>
                  <TableHead>{t("res.type")}</TableHead>
                  <TableHead>{t("res.period")}</TableHead>
                  <TableHead className="text-right">{t("res.rent")}</TableHead>
                  <TableHead className="text-right">{t("res.deposit")}</TableHead>
                  <TableHead>{t("res.status")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reservations.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">{t("res.empty")}</TableCell></TableRow>
                ) : reservations.map((r) => (
                  <TableRow key={r.id} className="hover:bg-[#FDFDFB]" data-testid={`res-row-${r.id}`}>
                    <TableCell className="font-semibold">{uName(r.unit_id)}</TableCell>
                    <TableCell>{tName(r.tenant_id)}</TableCell>
                    <TableCell>{t(`res.${r.rental_type}`)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(r.start_date)} → {r.end_date ? formatDate(r.end_date) : "…"}</TableCell>
                    <TableCell className="text-right font-semibold">{money(r.rent_amount)}</TableCell>
                    <TableCell className="text-right">{money(r.deposit_amount)}</TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                        <SelectTrigger className={`h-7 w-auto border-0 text-xs font-medium ${statusColor[r.status]}`} data-testid={`res-status-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["active", "upcoming", "ended", "cancelled"].map((s) => <SelectItem key={s} value={s}>{t(`res.${s}`)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setForm({ ...r })} data-testid={`res-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      {canDelete && <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`res-delete-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <ReservationTimeline reservations={reservations} tenants={tenants} units={units} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? t("res.edit") : t("res.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2"><Label>{t("res.tenant")}</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger data-testid="res-tenant-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{tenants.map((tn) => <SelectItem key={tn.id} value={tn.id}>{tn.first_name} {tn.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("res.unit")}</Label>
                <Select value={form.unit_id} onValueChange={(v) => { const u = units.find((x) => x.id === v); setForm({ ...form, unit_id: v, rent_amount: priceFor(u, form.rental_type), deposit_amount: form.deposit_amount || u?.base_rent || 0 }); }}>
                  <SelectTrigger data-testid="res-unit-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{units.filter((u) => u.type !== "conciergerie").map((u) => <SelectItem key={u.id} value={u.id}>{u.number} · {u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("res.type")}</Label>
                <Select value={form.rental_type} onValueChange={(v) => setForm({ ...form, rental_type: v, rent_amount: priceFor(selectedUnit, v) })}>
                  <SelectTrigger data-testid="res-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">{t("res.monthly")}</SelectItem><SelectItem value="weekly">{t("res.weekly")}</SelectItem><SelectItem value="daily">{t("res.daily")}</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("res.start")}</Label><Input type="date" value={form.start_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="res-start" /></div>
              <div className="space-y-2"><Label>{t("res.end")}</Label><Input type="date" value={form.end_date?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="res-end" /></div>
              <div className="space-y-2"><Label>{t("res.rent")} (฿)</Label><Input type="number" value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} data-testid="res-rent" /></div>
              <div className="space-y-2"><Label>{t("res.deposit")} (฿)</Label><Input type="number" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} data-testid="res-deposit" /></div>
              <div className="space-y-2 col-span-2"><Label>{t("res.status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="res-status-form"><SelectValue /></SelectTrigger>
                  <SelectContent>{["active", "upcoming", "ended", "cancelled"].map((s) => <SelectItem key={s} value={s}>{t(`res.${s}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={save} className="bg-teak hover:bg-teak-hover text-white" data-testid="res-save-btn">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
